import {
  useGetWorkspaceQuery,
  useRestoreFolderMutation,
} from '@carboncopyco/data';
import { RevertOutlineIcon } from '@carboncopyco/icons';
import type { WorkspaceFolder } from '@carboncopyco/types';
import {
  Button,
  TextCaption,
  Tooltip,
  TooltipBubble,
  notify,
} from '@carboncopyco/ui';
import { useFolderAnalytics } from '@carboncopyco/utils';
import { useRef } from 'react';
import { useWorkspacePageProps } from '../../../WorkspacePageContext';

interface FolderRestoreProps {
  folderId: WorkspaceFolder['id'];
  disabled?: boolean;
}
export const FolderRestore = ({ folderId, disabled }: FolderRestoreProps) => {
  const restoreButtonRef = useRef<HTMLDivElement>(null);
  const [restoreFolder] = useRestoreFolderMutation();
  const { organizationId, workspaceId } = useWorkspacePageProps();
  const { trackFolderClickRestore } = useFolderAnalytics('archive');
  const { data: workspace } = useGetWorkspaceQuery({
    organizationId: organizationId,
    id: workspaceId,
  });

  return (
    <TooltipBubble
      placement="top"
      title="Restore"
      disabled={disabled}
    >
      <div ref={restoreButtonRef}>
        <Button
          disabled={disabled}
          icon={<RevertOutlineIcon />}
          dark
          xSmall
          onClick={(event) => {
            event.stopPropagation();
            trackFolderClickRestore({
              workspaceId,
              folderId,
              workspaceName: workspace?.workspaceName,
              workspaceType: workspace?.workspaceType,
            });
            restoreFolder({
              organizationId,
              folderId,
              workspaceId,
            })
              .unwrap()
              .then(() => notify.success('Folder was restored successfully.'))
              .catch(() =>
                notify.error('Unable to restore the folder. Please try again.')
              );
          }}
        />
      </div>
      {disabled && (
        <Tooltip
          anchorRef={restoreButtonRef}
          placement="top"
        >
          <div className="p-2.5">
            <TextCaption
              label="To restore this folder, please delete at least one folder from your workspace as you have reached the maximum limit."
              color="text-ds-natural-400"
            />
          </div>
        </Tooltip>
      )}
    </TooltipBubble>
  );
};
